var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PortableWifiOff, Refresh } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { setBroadcastSelectedItem } from 'app/actions/map';
import { Scrollbar } from 'app/components';
import { useStateRef } from 'app/services';
import http from 'app/services/http';
import { BroadcastPlayerEvents, createBroadcastPlayer, getBroadcastDeviceId, isBroadcastPlayerSupported, resolveVideoAdapter, } from 'app/video';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
const createPreviewPlayers = (count) => Array.from({ length: count }, () => ({
    player: null,
    status: 'pending',
}));
const clearVideoPoster = (element) => {
    if (!element) {
        return;
    }
    const previous = element.poster;
    element.removeAttribute('poster');
    element.poster = '';
    if (previous && previous.startsWith('blob:')) {
        URL.revokeObjectURL(previous);
    }
};
const PREVIEW_FRAME_WAIT_MS = 4000;
const PREVIEW_FRAME_POLL_MS = 120;
const PREVIEW_NON_DARK_RATIO = 0.008;
const drawVideoFrame = (element) => {
    if (element.videoWidth === 0 || element.videoHeight === 0) {
        return null;
    }
    try {
        const canvas = document.createElement('canvas');
        canvas.width = element.videoWidth;
        canvas.height = element.videoHeight;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
            return null;
        }
        context.drawImage(element, 0, 0);
        return { canvas, context };
    }
    catch (_a) {
        return null;
    }
};
const isMostlyBlankFrame = (context, width, height) => {
    const sampleWidth = Math.min(48, width);
    const sampleHeight = Math.min(27, height);
    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = sampleWidth;
    sampleCanvas.height = sampleHeight;
    const sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true });
    if (!sampleContext) {
        return false;
    }
    sampleContext.drawImage(context.canvas, 0, 0, sampleWidth, sampleHeight);
    const { data } = sampleContext.getImageData(0, 0, sampleWidth, sampleHeight);
    let nonDark = 0;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i] > 28 || data[i + 1] > 28 || data[i + 2] > 28) {
            nonDark += 1;
        }
    }
    return nonDark / (sampleWidth * sampleHeight) < PREVIEW_NON_DARK_RATIO;
};
const silencePlayAbort = (element) => {
    if (element.__playAbortSilenced) {
        return;
    }
    const play = element.play.bind(element);
    element.play = () => play().catch((error) => {
        if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError')) {
            return undefined;
        }
        throw error;
    });
    element.__playAbortSilenced = true;
};
const waitNextVideoFrame = (element) => new Promise((resolve) => {
    if (typeof element.requestVideoFrameCallback === 'function') {
        element.requestVideoFrameCallback(() => resolve());
        return;
    }
    setTimeout(resolve, PREVIEW_FRAME_POLL_MS);
});
const waitForUsefulPreviewPoster = (element, signal, options) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const allowDark = Boolean(options === null || options === void 0 ? void 0 : options.allowDark);
    const deadline = Date.now() + ((_a = options === null || options === void 0 ? void 0 : options.waitMs) !== null && _a !== void 0 ? _a : PREVIEW_FRAME_WAIT_MS);
    let lastPoster = null;
    while (!(signal === null || signal === void 0 ? void 0 : signal.cancelled) && Date.now() < deadline) {
        if (element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && element.videoWidth > 0) {
            const frame = drawVideoFrame(element);
            if (frame) {
                const blank = isMostlyBlankFrame(frame.context, frame.canvas.width, frame.canvas.height);
                try {
                    const poster = frame.canvas.toDataURL('image/jpeg', 0.72);
                    lastPoster = poster;
                    // Dahua office cams are often near-black with OSD — still a valid poster.
                    if (!blank || allowDark) {
                        return poster;
                    }
                }
                catch (_b) {
                    // keep polling
                }
            }
        }
        yield Promise.race([waitNextVideoFrame(element), new Promise((resolve) => setTimeout(resolve, PREVIEW_FRAME_POLL_MS))]);
    }
    return lastPoster;
});
const MapTransportBroadcastList = function MapTransportBroadcastList() {
    var _a, _b;
    const dispatch = useDispatch();
    const broadcastRefs = useRef([]);
    const previewTimersRef = useRef([]);
    const channelTimersRef = useRef({});
    const broadcast = useSelector((state) => { var _a; return (_a = state.map.transportSelectedItem) === null || _a === void 0 ? void 0 : _a.broadcast; });
    const transportId = useSelector((state) => { var _a; return (_a = state.map.transportSelectedItem) === null || _a === void 0 ? void 0 : _a.id; });
    const { online } = broadcast !== null && broadcast !== void 0 ? broadcast : {};
    const deviceId = getBroadcastDeviceId(broadcast);
    const videoAdapter = resolveVideoAdapter(broadcast);
    const channels = ((_a = broadcast === null || broadcast === void 0 ? void 0 : broadcast.channels) === null || _a === void 0 ? void 0 : _a.length) ? broadcast.channels : ((_b = videoAdapter === null || videoAdapter === void 0 ? void 0 : videoAdapter.getChannels()) !== null && _b !== void 0 ? _b : []);
    const { transportBroadcastSelectedItem } = useSelector((state) => state.map);
    const [previewGeneration, setPreviewGeneration] = useState(0);
    const [broadcastPlayers, setBroadcastPlayers, broadcastPlayersRef] = useStateRef([]);
    const [posterUrls, setPosterUrls] = useState({});
    const posterUrlsRef = useRef(posterUrls);
    posterUrlsRef.current = posterUrls;
    const deviceIdRef = useRef(deviceId);
    const onlineRef = useRef(online);
    const adapterRef = useRef(videoAdapter);
    const channelsRef = useRef(channels);
    const transportIdRef = useRef(transportId);
    const previewStreamNamesRef = useRef({});
    const selectedChannelRef = useRef(transportBroadcastSelectedItem);
    deviceIdRef.current = deviceId;
    onlineRef.current = online;
    adapterRef.current = videoAdapter;
    channelsRef.current = channels;
    transportIdRef.current = transportId;
    selectedChannelRef.current = transportBroadcastSelectedItem;
    const releasePreviewStream = (key) => {
        var _a, _b;
        const streamName = previewStreamNamesRef.current[key];
        const tid = transportIdRef.current;
        delete previewStreamNamesRef.current[key];
        if (streamName && tid != null) {
            (_b = (_a = adapterRef.current) === null || _a === void 0 ? void 0 : _a.releaseLive) === null || _b === void 0 ? void 0 : _b.call(_a, { transportId: tid, streamName });
        }
    };
    const handleItemClick = (value) => dispatch(setBroadcastSelectedItem(value));
    const clearChannelTimers = (key) => {
        const timers = channelTimersRef.current[key];
        if (!timers) {
            return;
        }
        timers.forEach((timerId) => clearTimeout(timerId));
        delete channelTimersRef.current[key];
    };
    const unloadChannelPlayer = (key) => {
        var _a;
        const currentPlayer = (_a = broadcastPlayersRef.current[key]) === null || _a === void 0 ? void 0 : _a.player;
        const element = broadcastRefs.current[key];
        releasePreviewStream(key);
        if (!currentPlayer) {
            return;
        }
        try {
            element === null || element === void 0 ? void 0 : element.pause();
        }
        catch (_b) {
            // ignore pause races during teardown
        }
        currentPlayer.unload();
        currentPlayer.off();
        currentPlayer.detachMediaElement();
    };
    const loadChannelPreview = (key) => {
        if (!deviceIdRef.current || !adapterRef.current || onlineRef.current === false) {
            return;
        }
        const element = broadcastRefs.current[key];
        if (!element) {
            return;
        }
        clearChannelTimers(key);
        unloadChannelPlayer(key);
        clearVideoPoster(element);
        silencePlayAbort(element);
        const loadSignal = { cancelled: false };
        setBroadcastPlayers(broadcastPlayersRef.current.map((currentPlayer, currentKey) => ({
            player: currentKey === key ? null : currentPlayer.player,
            status: currentKey === key ? 'pending' : currentPlayer.status,
        })));
        const adapter = adapterRef.current;
        const currentDeviceId = deviceIdRef.current;
        const channel = channelsRef.current[key];
        if (!adapter || currentDeviceId == null || !channel) {
            return;
        }
        const setChannelStatus = (status) => {
            setBroadcastPlayers(broadcastPlayersRef.current.map((currentPlayer, currentKey) => (Object.assign(Object.assign({}, currentPlayer), { status: currentKey === key ? status : currentPlayer.status }))));
        };
        if (adapter.supportsPreviewPosters && typeof adapter.getPreviewPosterUrl === 'function') {
            const startPosterPreview = () => __awaiter(this, void 0, void 0, function* () {
                if (posterUrlsRef.current[key]) {
                    setChannelStatus('fulfilled');
                    return;
                }
                // Selected channel is owned by the main live player — never fetch JPEG from its source
                // (frame.jpeg on the live stream can stall first frame). Tile gets poster from live later.
                if (adapter.skipPosterFetchWhenSelected && String(channel.id) === String(selectedChannelRef.current)) {
                    setChannelStatus('fulfilled');
                    return;
                }
                const posterPath = adapter.getPreviewPosterUrl({
                    deviceId: currentDeviceId,
                    channelId: channel.id,
                    transportId: transportIdRef.current,
                });
                if (!posterPath) {
                    setChannelStatus('rejected');
                    return;
                }
                try {
                    const { data, headers } = yield http.get(posterPath, {
                        responseType: 'blob',
                        timeout: 60000,
                    });
                    if (loadSignal.cancelled) {
                        return;
                    }
                    const contentType = String((headers === null || headers === void 0 ? void 0 : headers['content-type']) || (data === null || data === void 0 ? void 0 : data.type) || '');
                    const looksLikeImage = !contentType || contentType.includes('image') || contentType.includes('octet-stream') || contentType.includes('jpeg');
                    if (!(data instanceof Blob) || data.size < 200 || !looksLikeImage) {
                        setChannelStatus('rejected');
                        return;
                    }
                    const objectUrl = URL.createObjectURL(data);
                    setPosterUrls((prev) => {
                        const previous = prev[key];
                        if (previous && previous.startsWith('blob:')) {
                            URL.revokeObjectURL(previous);
                        }
                        return Object.assign(Object.assign({}, prev), { [key]: objectUrl });
                    });
                    setChannelStatus('fulfilled');
                }
                catch (_a) {
                    if (!loadSignal.cancelled) {
                        setChannelStatus('rejected');
                    }
                }
            });
            startPosterPreview().catch(() => {
                if (!loadSignal.cancelled) {
                    setChannelStatus('rejected');
                }
            });
            return;
        }
        if (adapter.supportsPreviewStreams === false) {
            setChannelStatus('fulfilled');
            return;
        }
        if (!isBroadcastPlayerSupported()) {
            setChannelStatus('fulfilled');
            return;
        }
        const startPreview = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            let playback;
            try {
                playback = yield adapter.getPreviewPlayback({
                    deviceId: currentDeviceId,
                    channelId: channel.id,
                    transportId: transportIdRef.current,
                });
            }
            catch (_b) {
                if (!loadSignal.cancelled) {
                    setChannelStatus('rejected');
                }
                return;
            }
            if (loadSignal.cancelled || !playback || !broadcastRefs.current[key]) {
                return;
            }
            previewStreamNamesRef.current[key] = playback.streamName;
            const player = createBroadcastPlayer(playback, {
                media: {
                    duration: 1,
                },
            });
            const clearPlayerRef = () => {
                setBroadcastPlayers(broadcastPlayersRef.current.map((currentPlayer, currentKey) => (Object.assign(Object.assign({}, currentPlayer), { player: currentKey === key ? null : currentPlayer.player }))));
            };
            const disposePlayer = () => {
                loadSignal.cancelled = true;
                try {
                    element.pause();
                }
                catch (_a) {
                    // ignore pause races during teardown
                }
                player.off();
                player.unload();
                player.detachMediaElement();
                clearPlayerRef();
                releasePreviewStream(key);
            };
            const timers = [];
            const pendingMs = config.mapBroadcastPreviewPendingTimeout;
            const pendingTimerId = setTimeout(() => {
                const current = broadcastPlayersRef.current[key];
                if ((current === null || current === void 0 ? void 0 : current.status) === 'pending' && current.player === player) {
                    setChannelStatus('rejected');
                    disposePlayer();
                }
            }, pendingMs);
            timers.push(pendingTimerId);
            const firstFrame = new Promise((resolve) => {
                let settled = false;
                const settle = (value) => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    resolve(value);
                };
                player.on(BroadcastPlayerEvents.MEDIA_INFO, () => settle('ready'));
                player.on(BroadcastPlayerEvents.ERROR, (reason) => {
                    if (reason === 'NETWORK_ERROR' || reason === 'MEDIA_ERROR') {
                        settle('error');
                    }
                });
            });
            player.attachMediaElement(element);
            player.load();
            setBroadcastPlayers(broadcastPlayersRef.current.map((currentPlayer, currentKey) => ({
                player: currentKey === key ? player : currentPlayer.player,
                status: currentPlayer.status,
            })));
            channelTimersRef.current[key] = timers;
            const outcome = yield Promise.race([firstFrame, new Promise((resolve) => setTimeout(() => resolve('error'), pendingMs))]);
            if (outcome === 'error') {
                if (!loadSignal.cancelled) {
                    clearTimeout(pendingTimerId);
                    setChannelStatus('rejected');
                    disposePlayer();
                }
                return;
            }
            if (loadSignal.cancelled) {
                return;
            }
            const poster = yield waitForUsefulPreviewPoster(element, loadSignal, {
                allowDark: false,
                waitMs: PREVIEW_FRAME_WAIT_MS,
            });
            if (loadSignal.cancelled || ((_a = broadcastPlayersRef.current[key]) === null || _a === void 0 ? void 0 : _a.player) !== player) {
                return;
            }
            clearTimeout(pendingTimerId);
            if (poster) {
                element.poster = poster;
            }
            setChannelStatus('fulfilled');
            try {
                player.pause();
                element.pause();
            }
            catch (_c) {
                // ignore pause races during teardown
            }
            const stopTimerId = setTimeout(() => {
                var _a;
                if (loadSignal.cancelled || ((_a = broadcastPlayersRef.current[key]) === null || _a === void 0 ? void 0 : _a.player) !== player) {
                    return;
                }
                // Без poster unload оставляет пустой tile — оставляем paused MSE.
                if (poster) {
                    disposePlayer();
                }
            }, config.mapBroadcastTimeout);
            timers.push(stopTimerId);
            channelTimersRef.current[key] = timers;
        });
        startPreview().catch(() => {
            if (!loadSignal.cancelled) {
                setChannelStatus('rejected');
            }
        });
    };
    const handlePreviewRefreshAll = () => setPreviewGeneration((value) => value + 1);
    const handleChannelRefresh = (event, key) => {
        event.stopPropagation();
        loadChannelPreview(key);
    };
    useEffect(() => {
        previewTimersRef.current.forEach((timerId) => clearTimeout(timerId));
        previewTimersRef.current = [];
        Object.keys(channelTimersRef.current).forEach((key) => clearChannelTimers(Number(key)));
        if (online === false) {
            broadcastPlayersRef.current.forEach((currentPlayer, key) => {
                var _a;
                if (currentPlayer.player) {
                    try {
                        (_a = broadcastRefs.current[key]) === null || _a === void 0 ? void 0 : _a.pause();
                    }
                    catch (_b) {
                        // ignore pause races during teardown
                    }
                    currentPlayer.player.unload();
                    currentPlayer.player.off();
                    currentPlayer.player.detachMediaElement();
                }
                clearVideoPoster(broadcastRefs.current[key]);
            });
            setBroadcastPlayers(channelsRef.current.map(() => ({
                player: null,
                status: 'rejected',
            })));
            return;
        }
        if (!deviceId || !videoAdapter) {
            return;
        }
        if (videoAdapter.supportsPreviewStreams === false && !videoAdapter.supportsPreviewPosters) {
            setBroadcastPlayers(channelsRef.current.map(() => ({
                player: null,
                status: 'fulfilled',
            })));
            return;
        }
        if (videoAdapter.supportsPreviewStreams !== false && !isBroadcastPlayerSupported()) {
            return;
        }
        setBroadcastPlayers(createPreviewPlayers(channelsRef.current.length));
        setPosterUrls((prev) => {
            Object.values(prev).forEach((url) => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            return {};
        });
        // Optional: wait for first live still before kicking JPEG tiles (avoids stealing upstream from live).
        if (videoAdapter.deferPreviewsUntilLivePoster && videoAdapter.livePosterBus) {
            let kicked = false;
            const kickPreviews = () => {
                if (kicked) {
                    return;
                }
                kicked = true;
                channelsRef.current.forEach((_, key) => loadChannelPreview(key));
            };
            let skipReplay = Boolean(videoAdapter.livePosterBus.get());
            const unsubLivePoster = videoAdapter.livePosterBus.subscribe((payload) => {
                if (skipReplay) {
                    skipReplay = false;
                    return;
                }
                if (payload === null || payload === void 0 ? void 0 : payload.posterUrl) {
                    kickPreviews();
                }
            });
            const kickTimerId = setTimeout(kickPreviews, videoAdapter.previewKickTimeoutMs || 1400);
            previewTimersRef.current.push(kickTimerId);
            return () => {
                unsubLivePoster();
                previewTimersRef.current.forEach((timerId) => clearTimeout(timerId));
                previewTimersRef.current = [];
                Object.keys(channelTimersRef.current).forEach((channelKey) => clearChannelTimers(Number(channelKey)));
                broadcastPlayersRef.current.forEach((currentPlayer, key) => {
                    var _a;
                    releasePreviewStream(key);
                    if (!currentPlayer.player) {
                        return;
                    }
                    try {
                        (_a = broadcastRefs.current[key]) === null || _a === void 0 ? void 0 : _a.pause();
                    }
                    catch (_b) {
                        // ignore pause races during teardown
                    }
                    currentPlayer.player.unload();
                    currentPlayer.player.off();
                    currentPlayer.player.detachMediaElement();
                });
            };
        }
        channelsRef.current.forEach((_, key) => {
            const staggerTimerId = setTimeout(() => loadChannelPreview(key), key * config.mapBroadcastPreviewStagger);
            previewTimersRef.current.push(staggerTimerId);
        });
        return () => {
            previewTimersRef.current.forEach((timerId) => clearTimeout(timerId));
            previewTimersRef.current = [];
            Object.keys(channelTimersRef.current).forEach((channelKey) => clearChannelTimers(Number(channelKey)));
            broadcastPlayersRef.current.forEach((currentPlayer, key) => {
                var _a;
                releasePreviewStream(key);
                if (!currentPlayer.player) {
                    return;
                }
                try {
                    (_a = broadcastRefs.current[key]) === null || _a === void 0 ? void 0 : _a.pause();
                }
                catch (_b) {
                    // ignore pause races during teardown
                }
                currentPlayer.player.unload();
                currentPlayer.player.off();
                currentPlayer.player.detachMediaElement();
            });
        };
    }, [deviceId, online, previewGeneration, transportId]);
    // Optional: canvas still from live if JPEG not ready yet.
    useEffect(() => {
        if (!(videoAdapter === null || videoAdapter === void 0 ? void 0 : videoAdapter.livePosterBus)) {
            return undefined;
        }
        return videoAdapter.livePosterBus.subscribe((payload) => {
            if (!payload) {
                return;
            }
            const key = channelsRef.current.findIndex((channel) => String(channel.id) === String(payload.channelId));
            if (key < 0 || posterUrlsRef.current[key]) {
                return;
            }
            setPosterUrls((prev) => (Object.assign(Object.assign({}, prev), { [key]: payload.posterUrl })));
            setBroadcastPlayers(broadcastPlayersRef.current.map((currentPlayer, currentKey) => (Object.assign(Object.assign({}, currentPlayer), { status: currentKey === key ? 'fulfilled' : currentPlayer.status }))));
        });
    }, [videoAdapter, deviceId]);
    return (React.createElement(Stack, { direction: 'column', sx: { height: '100%' } },
        React.createElement(Stack, { direction: 'row', alignItems: 'center', justifyContent: 'space-between', sx: (theme) => ({
                flex: '0 0 auto',
                px: theme.spacing(2),
                pt: theme.spacing(1),
                pb: 0,
            }) },
            React.createElement(Typography, { variant: 'caption', color: 'text.secondary' }, "\u041F\u0440\u0435\u0432\u044C\u044E \u043A\u0430\u043C\u0435\u0440"),
            React.createElement(Tooltip, { title: '\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u043F\u0440\u0435\u0432\u044C\u044E \u0432\u0441\u0435\u0445 \u043A\u0430\u043C\u0435\u0440', placement: 'top' },
                React.createElement("span", { style: { display: 'inline-flex' } },
                    React.createElement(IconButton, { size: 'small', onClick: handlePreviewRefreshAll, disabled: online === false },
                        React.createElement(Refresh, { fontSize: 'small' }))))),
        React.createElement(Scrollbar, null,
            React.createElement(Box, { sx: (theme) => ({
                    display: 'flex',
                    flexWrap: 'wrap',
                    width: '100%',
                    height: '100%',
                    gap: theme.spacing(1),
                    padding: theme.spacing(1, 2, 2),
                }) }, channels.map((channel, key) => {
                var _a, _b;
                return (React.createElement(Stack, { key: String(channel.id), direction: 'column', alignItems: 'center', justifyContent: 'center', sx: (theme) => ({
                        maxWidth: '50%',
                        height: '80px',
                        flex: `1 1 calc((100% / 2) - ${theme.spacing(1)})`,
                    }) },
                    React.createElement(Stack, { direction: 'column', alignItems: 'center', justifyContent: 'center', onClick: () => handleItemClick(channel.id), sx: (theme) => (Object.assign({ width: '100%', height: '100%', position: 'relative', bgcolor: 'background.default', borderRadius: theme.shape.borderRadius, overflow: 'hidden', border: '2px solid', borderColor: 'transparent', cursor: 'pointer', video: {
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            } }, (transportBroadcastSelectedItem === channel.id && {
                            borderColor: theme.palette.primary.main,
                        }))) },
                        React.createElement("video", { id: String(channel.id), ref: (el) => {
                                broadcastRefs.current[key] = el;
                            } }),
                        posterUrls[key] && (React.createElement(Box, { component: 'img', src: posterUrls[key], alt: '', sx: {
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                pointerEvents: 'none',
                            } })),
                        React.createElement(Stack, { direction: 'column', alignItems: 'center', justifyContent: 'center', sx: {
                                width: '100%',
                                height: '100%',
                                position: 'absolute',
                                top: '0',
                                left: '0',
                                pointerEvents: 'none',
                                'svg:not(.MuiCircularProgress-svg)': {
                                    color: 'text.secondary',
                                },
                            } },
                            ((_a = broadcastPlayers[key]) === null || _a === void 0 ? void 0 : _a.status) === 'rejected' && !posterUrls[key] && React.createElement(PortableWifiOff, { fontSize: 'small' }),
                            ((_b = broadcastPlayers[key]) === null || _b === void 0 ? void 0 : _b.status) === 'pending' && !posterUrls[key] && React.createElement(CircularProgress, { size: 18 }))),
                    React.createElement(Stack, { direction: 'row', alignItems: 'center', spacing: 0.25, sx: { width: '100%', justifyContent: 'center' } },
                        React.createElement(Typography, { component: 'span', variant: 'caption' }, channel.label),
                        React.createElement(Tooltip, { title: '\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u043F\u0440\u0435\u0432\u044C\u044E \u043A\u0430\u043C\u0435\u0440\u044B', placement: 'top' },
                            React.createElement("span", { style: { display: 'inline-flex' } },
                                React.createElement(IconButton, { size: 'small', disabled: online === false, onClick: (event) => handleChannelRefresh(event, key), sx: { p: 0.25 } },
                                    React.createElement(Refresh, { sx: { fontSize: 14 } })))))));
            })))));
};
export default MapTransportBroadcastList;
